import React, { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "./button";
import AuthService from "../../services/auth-service";
import { useAuth } from "../../contexts/AuthContext";

type SignInProps = {
  onClose?: () => void;
  onSwitchToSignup?: () => void;
};

export function SignIn({ onClose, onSwitchToSignup }: SignInProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await AuthService.loginWithGoogle();
      // For OAuth flows, we redirect the user to the provider
      // The result.success just indicates the redirect was initiated
      if (result.success) {
        // Redirect will be handled by AuthService
        // We don't need to do anything else here
      } else {
        setError(result.error || "Google login failed");
      }
    } catch (err) {
      setError("An error occurred during Google login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGitHubLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await AuthService.loginWithGitHub();
      // For OAuth flows, we redirect the user to the provider
      // The result.success just indicates the redirect was initiated
      if (result.success) {
        // Redirect will be handled by AuthService
        // We don't need to do anything else here
      } else {
        setError(result.error || "GitHub login failed");
      }
    } catch (err) {
      setError("An error occurred during GitHub login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-foreground">Sign In to Caude</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <img 
                alt="Google logo" 
                className="w-5 h-5 mr-2" 
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuATFGAQbNgdpHWu_tzISSRW7ZlhrzRHDocD_C75iWXmmiwxCB2-0IRDxURkhm7FO4odsqD9cmjnRsw_f5jKCN3arKnDPHK5DhvEF2K4Zwzv1sKLuiNqgIuoALPso3npk1Z08QZn0hhQBOAH5NyHPpKGnoD_UH-_yNh0oUW789pFrEQr95FB0DdDKYPKKSGWumOn8CNw3TJRhbNAuRZIjoYtENjiLHOq4MlJkWsjOhi_DVGVlnvve2hvHN6ivljvY879fNZtIvd-qHjD"
              />
              Continue with Google
            </Button>
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center"
              onClick={handleGitHubLogin}
              disabled={isLoading}
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.91 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"></path>
              </svg>
              Continue with GitHub
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              By continuing, you agree to our <br/>
              <a className="underline hover:text-foreground" href="#">Terms of Service</a> and <a className="underline hover:text-foreground" href="#">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}